/**
 * Metrics log pool — same permission + text search as /logs, but without
 * combine SKU, activity scope, time range, or cash/units filters.
 */

import {
  buildPermissionByActor,
  canViewActivityLog,
  sliceLogsByStartEndLogId,
  hasLogUnits,
  isExpenseBalanceLog,
} from 'pages/Logs/activityLogsDisplay';
import type { ActivityLogsDisplayLogsParams } from 'pages/Logs/activityLogsDisplay';
import {
  buildManagerLogAccessContext,
} from 'pages/Logs/managerLogAccess';
import {
  isDepartmentManagerLogViewer,
} from 'pages/Settings/redux';
import {
  collectLogSearchQueries,
  filterLogsBySearch,
} from 'pages/Logs/logSearch';
import type { Log } from 'pages/Logs/hooks';

export type BuildMetricsActivityLogsParams = Omit<
ActivityLogsDisplayLogsParams,
'useTimeRange' | 'startDate' | 'endDate' | 'allItems' | 'cashIn' | 'cashOut' | 'checked'
> & {
  /** When false, skip /logs text-search chips — metrics uses its own scope picks. */
  applyActivityLogsSearch?: boolean,
};

/** Permission + search pipeline only — metrics applies its own scope/time/cash later. */
export const buildMetricsActivityLogs = (
  params: BuildMetricsActivityLogsParams,
): Log[] => {
  const {
    logs,
    permission,
    staffId,
    username,
    users,
    erpRecords,
    logSearches,
    logSearch,
    logSearchOptions,
    staffMaps,
    customerMaps,
    departmentNames,
    searchArchives,
    onlyLogIds,
    startLogId,
    endLogId,
    associateElevatedActivityLogs,
    isComplianceActivity,
    complianceActivityStaffId,
    applyActivityLogsSearch = false,
  } = params;

  const onlyIdSet = onlyLogIds?.length
    ? new Set(onlyLogIds.map((id) => String(id)))
    : null;

  const permissionByActor = buildPermissionByActor(users);
  const managerContext = isDepartmentManagerLogViewer(permission)
    ? buildManagerLogAccessContext(erpRecords || [], users, staffId, username)
    : null;
  // Metrics never applies /logs time toggle — compliance elevation stays off here.
  const complianceReviewActive = false;

  const queries = applyActivityLogsSearch
    ? collectLogSearchQueries(logSearches, logSearch, logSearchOptions)
    : [];
  const searchBasedLogs = onlyIdSet
    ? (logs || []).filter((l) => onlyIdSet.has(String(l.id)))
    : (queries.length
      ? filterLogsBySearch(
        logs || [],
        queries,
        staffMaps,
        searchArchives,
        departmentNames,
        customerMaps,
      )
      : (logs || []));

  const permissionFiltered = onlyIdSet
    ? searchBasedLogs
    : searchBasedLogs.filter((l) => canViewActivityLog(l, {
      permission,
      staffId,
      username,
      staffMaps,
      permissionByActor,
      complianceReviewActive,
      managerContext,
    }));

  const dateSortedLogs = [...permissionFiltered];
  dateSortedLogs.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  const idRangeLogs = onlyIdSet
    ? dateSortedLogs
    : sliceLogsByStartEndLogId(dateSortedLogs, startLogId, endLogId);

  if (onlyIdSet) return idRangeLogs;

  if (isComplianceActivity && complianceActivityStaffId) {
    const logIsFromComplianceCashier = (log: { created_by?: string | null }) => (
      String(log.created_by || '') === String(complianceActivityStaffId)
    );
    let firstMs: number | null = null;
    idRangeLogs.forEach((log) => {
      if (!logIsFromComplianceCashier(log)) return;
      if (!hasLogUnits(log)) return;
      if (!(Number(log.value) > 0)) return;
      const t = +new Date(log.date);
      if (Number.isNaN(t)) return;
      if (firstMs == null || t < firstMs) firstMs = t;
    });
    if (firstMs != null) {
      return idRangeLogs.filter((log) => {
        if (isExpenseBalanceLog(log)) return false;
        if (!hasLogUnits(log)) return false;
        const t = +new Date(log.date);
        if (Number.isNaN(t) || t < firstMs!) return true;
        return logIsFromComplianceCashier(log);
      });
    }
  }

  if (isComplianceActivity) {
    return idRangeLogs.filter((log) => {
      if (isExpenseBalanceLog(log)) return false;
      return hasLogUnits(log);
    });
  }

  return idRangeLogs;
};
